/**
 * After a frontend deploy an open tab still runs the old build, whose lazy
 * chunks are gone: a dynamic import fails with "Failed to fetch dynamically
 * imported module" and the section never opens. Vite reports it as
 * `vite:preloadError`; the tab reloads once to pick up the new build (G37).
 *
 * The error is not swallowed: if the reload is held up (the designer asks
 * before leaving unsaved work), the caller still sees it. A second failure
 * within a minute of the reload is a really broken chunk, not a stale one, and
 * is left to surface instead of reloading in a loop.
 */

const RELOADED_AT_KEY = 'fz_stale_chunk_reloaded_at';
const LOOP_GUARD_MS = 60_000;

export function installStaleChunkReload(
  target: Pick<Window, 'addEventListener' | 'sessionStorage' | 'location'> = window,
  now: () => number = Date.now,
): void {
  target.addEventListener('vite:preloadError', () => {
    try {
      const last = Number(target.sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
      if (now() - last < LOOP_GUARD_MS) return;
      target.sessionStorage.setItem(RELOADED_AT_KEY, String(now()));
    } catch {
      // Without the guard a broken chunk would reload forever.
      return;
    }
    target.location.reload();
  });
}
